import omit from 'lodash/fp/omit';
import flow from 'lodash/fp/flow';
import update from 'lodash/fp/update';
import map from 'lodash/fp/map';
import {
  excludeWeekdaysDefault,
  getDateRangeFromScheduleRules,
  getScheduleTotalHours,
  getWorkingDaysCount,
  hasOverlap,
  shouldExcludeDay
} from '~/modules/resourcing/common/util/scheduleUtil';
import {
  mapRepliconDateToUtcObject,
  mapIsoStringtoUtcObject
} from '~/modules/common/dates/convert';
import { appendScheduleRuleTypeNameFields } from '~/modules/resourcing/common/util/resourceAllocationUtil';

export const omitForbiddenFields = flow(
  update(
    'scheduleRules',
    map(
      flow(
        omit('__typename'),
        update('dateRange', omit('__typename')),
        update('do', flow(omit('__typename')))
      )
    )
  )
);

export const getHoursForDayFromCurrentDayRule = ({ currentDayRule, day }) => {
  if (!currentDayRule) return 0;

  return shouldExcludeDay(day, currentDayRule.do.excludeWeekdays)
    ? 0
    : currentDayRule.do.setHours * ((currentDayRule.do.load || 100) / 100);
};

const getOverlappingScheduleRule = ({ rules, currentSchedule, currentDay }) => {
  if (
    !currentSchedule?.dateRange ||
    currentDay > mapIsoStringtoUtcObject(currentSchedule.dateRange.startDate)
  ) {
    return rules.find(rule =>
      hasOverlap({ start: currentDay, end: currentDay })(rule)
    );
  }

  return currentSchedule;
};

export const getProjectRemainingHoursPerDay = ({
  resourceAllocationScheduleRules,
  otherTaskAllocationsSummaryScheduleRules,
  startDate,
  endDate
}) => {
  const firstDate = mapRepliconDateToUtcObject(startDate);
  const lastDate = mapRepliconDateToUtcObject(endDate);

  const projectRemainingHoursPerDay = [];
  let totalProjectRemainingHours = 0;
  let currentDay = firstDate;
  let overlappingScheduleRuleFromResourceAllocation = null;
  let overlappingScheduleRuleFromOtherTaskAllocations = null;

  while (currentDay <= lastDate) {
    overlappingScheduleRuleFromResourceAllocation = getOverlappingScheduleRule({
      rules: resourceAllocationScheduleRules,
      currentSchedule: overlappingScheduleRuleFromResourceAllocation,
      currentDay
    });

    const resourceAllocationHours = getHoursForDayFromCurrentDayRule({
      day: currentDay,
      currentDayRule: overlappingScheduleRuleFromResourceAllocation
    });

    let otherTaskAllocationHours = 0;

    if (
      resourceAllocationHours &&
      otherTaskAllocationsSummaryScheduleRules?.length
    ) {
      overlappingScheduleRuleFromOtherTaskAllocations = getOverlappingScheduleRule(
        {
          rules: otherTaskAllocationsSummaryScheduleRules,
          currentSchedule: overlappingScheduleRuleFromOtherTaskAllocations,
          currentDay
        }
      );
      otherTaskAllocationHours = getHoursForDayFromCurrentDayRule({
        day: currentDay,
        currentDayRule: overlappingScheduleRuleFromOtherTaskAllocations
      });
    }

    const availableHours = Math.max(
      0,
      resourceAllocationHours - otherTaskAllocationHours
    );

    totalProjectRemainingHours += availableHours;

    projectRemainingHoursPerDay.push({
      date: currentDay,
      hours: availableHours
    });

    currentDay = currentDay.plus({ days: 1 });
  }

  return {
    projectRemainingHoursPerDay,
    totalProjectRemainingHours
  };
};

export const getScheduleRulesForOptimisticResponse = ({
  periodStartDate,
  periodEndDate,
  updatedTaskAllocationPeriodHours
}) => {
  const numberOfWorkingDaysInPeriod = getWorkingDaysCount({
    start: periodStartDate,
    end: periodEndDate,
    excludeWeekdays: excludeWeekdaysDefault
  });

  const daysCount =
    numberOfWorkingDaysInPeriod ||
    periodEndDate.diff(periodStartDate, 'days').toObject().days + 1;

  return [
    {
      dateRange: {
        startDate: periodStartDate.toISO(),
        endDate: periodEndDate.toISO(),
        __typename: 'DateRange'
      },
      do: {
        load: 100,
        setHours: updatedTaskAllocationPeriodHours / daysCount,
        excludeWeekdays: numberOfWorkingDaysInPeriod
          ? excludeWeekdaysDefault
          : []
      },
      __typename: 'ScheduleRule'
    }
  ];
};

const getOptimisticTaskAllocation = ({
  id,
  taskResourceEstimate,
  projectId,
  userId,
  scheduleRules
}) => {
  const { startDate, endDate } = getDateRangeFromScheduleRules(scheduleRules);

  const totalHours = getScheduleTotalHours({
    scheduleRules,
    quantity: 1
  });

  return {
    __typename: 'TaskResourceUserAllocation',
    id,
    taskUri: taskResourceEstimate.task.id,
    projectUri: projectId,
    allocationUserUri: userId,
    roleUri: taskResourceEstimate.projectRole?.id || null,
    scheduleRules: (scheduleRules || []).map(appendScheduleRuleTypeNameFields),
    totalHours,
    taskResourceEstimateUri: taskResourceEstimate.estimateUri,
    resourceAllocationUri: null,
    startDate,
    endDate
  };
};

export const buildUpdateTaskResourceUserAllocationOptimisticResponse = ({
  id,
  taskResourceEstimate,
  projectId,
  userId,
  scheduleRules
}) => ({
  __typename: 'Mutation',
  updateTaskResourceUserAllocation: {
    __typename: 'UpdateTaskResourceUserAllocationResult',
    taskResourceUserAllocation: getOptimisticTaskAllocation({
      id,
      taskResourceEstimate,
      projectId,
      userId,
      scheduleRules
    }),
    syncedResourceAllocation: null
  }
});

export const buildCreateTaskResourceUserAllocationOptimisticResponse = ({
  id,
  taskResourceEstimate,
  projectId,
  userId,
  scheduleRules
}) => ({
  __typename: 'Mutation',
  createTaskResourceUserAllocation: {
    __typename: 'CreateTaskResourceUserAllocationResult',
    taskResourceUserAllocation: getOptimisticTaskAllocation({
      id,
      taskResourceEstimate,
      projectId,
      userId,
      scheduleRules
    }),
    syncedResourceAllocation: null
  }
});
